import { singleton } from 'tsyringe';
import { LocalStorage } from 'app-core/src/utils/storage/local-storage';
import { LoggerService } from '../logger.service';
import {
  RobloxExperience,
  RobloxExperienceHistory,
  RobloxExperienceSummary,
} from './models/roblox-experience-history';

const kNameOfRobloxExperienceHistory = 'roblox-experience-history';
const kMsInDay = 24 * 60 * 60 * 1000;
const kTopExperiencesWindowInDays = 14;

// -----------------------------------------------------------------------------
@singleton()
export class RobloxExperienceHistoryService {
  private readonly logger = LoggerService.getCategory(
    'RobloxExperienceHistoryService',
  );

  // ---------------------------------------------------------------------------
  public recordSession(
    experienceId: string,
    placeId: string | undefined,
    startTS: number,
    durationInSeconds: number,
  ): void {
    if (!experienceId) {
      return;
    }

    const history = this.getHistory();

    let experience = history.experiences[experienceId];
    if (!experience) {
      experience = { experienceId, placeId, sessions: [] };
      history.experiences[experienceId] = experience;
    }

    if (placeId && !experience.placeId) {
      experience.placeId = placeId;
    }

    experience.sessions.push({ startTS, durationInSeconds });

    this.setHistory(history);
    this.logger.info(
      'Recorded session',
      JSON.stringify({
        experienceId,
        placeId,
        startTS,
        durationInSeconds,
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // Returns up to `topX` experiences played within the last two weeks,
  // ordered by total aggregated playtime (most-played first). Omit `topX`
  // to return every recent experience.
  public getTopExperiences(topX?: number): RobloxExperienceSummary[] {
    const history = this.getHistory();
    const cutoffTS = Date.now() - kTopExperiencesWindowInDays * kMsInDay;

    const summaries: RobloxExperienceSummary[] = [];

    for (const experienceId of Object.keys(history.experiences)) {
      const experience = history.experiences[experienceId];
      const summary = this.summarizeSince(experience, cutoffTS);
      if (summary.totalDurationInSeconds > 0) {
        summaries.push(summary);
      }
    }

    summaries.sort(
      (a, b) => b.totalDurationInSeconds - a.totalDurationInSeconds,
    );

    return topX != null ? summaries.slice(0, topX) : summaries;
  }

  // ---------------------------------------------------------------------------
  public getHistory(): RobloxExperienceHistory {
    const history = LocalStorage.get(
      kNameOfRobloxExperienceHistory,
    ).asObject() as RobloxExperienceHistory;

    if (history && history.experiences) {
      return history;
    }

    return { experiences: {} };
  }

  // ---------------------------------------------------------------------------
  public resetHistory(): void {
    LocalStorage.remove(kNameOfRobloxExperienceHistory);
  }

  // ---------------------------------------------------------------------------
  private summarizeSince(
    experience: RobloxExperience,
    cutoffTS: number,
  ): RobloxExperienceSummary {
    let totalDurationInSeconds = 0;
    let sessionCount = 0;
    let lastStartTS = 0;

    for (const session of experience.sessions) {
      if (session.startTS < cutoffTS) {
        continue;
      }

      totalDurationInSeconds += session.durationInSeconds;
      sessionCount += 1;
      if (session.startTS > lastStartTS) {
        lastStartTS = session.startTS;
      }
    }

    return {
      experienceId: experience.experienceId,
      placeId: experience.placeId,
      totalDurationInSeconds,
      sessionCount,
      lastStartTS,
    };
  }

  // ---------------------------------------------------------------------------
  private setHistory(history: RobloxExperienceHistory): void {
    LocalStorage.setObject(kNameOfRobloxExperienceHistory, history);
  }
}
